import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa";
import { servicesData } from "../../public/data/ServicesData";
import ServiceCard from "./ServiceCard";

const cardVariants = {
	hidden: {
		opacity: 0,
		y: 30,
		scale: 0.95,
	},
	visible: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.6,
			ease: "easeOut",
		},
	},
	hover: {
		scale: 1.02,
		y: -5,
		transition: {
			duration: 0.3,
			ease: "easeInOut",
		},
	},
};

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.2,
		},
	},
};

function ServicesSection({ isCarousel }) {
	const [index, setIndex] = useState(servicesData.length);
	const [itemsPerView, setItemsPerView] = useState(1);
	const [touchStart, setTouchStart] = useState(0);
	const [touchEnd, setTouchEnd] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(true);

	const extendedServices = [...servicesData, ...servicesData, ...servicesData];

	useEffect(() => {
		const mq = window.matchMedia("(min-width: 768px)");
		const update = () => setItemsPerView(mq.matches ? 3 : 1);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);

	const handleAnimationComplete = () => {
		if (index >= servicesData.length * 2) {
			setIsTransitioning(false);
			setIndex(index - servicesData.length);
		} else if (index < servicesData.length) {
			setIsTransitioning(false);
			setIndex(index + servicesData.length);
		}
	};

	useEffect(() => {
		if (!isTransitioning) {
			const timeout = setTimeout(() => setIsTransitioning(true), 10);
			return () => clearTimeout(timeout);
		}
	}, [isTransitioning]);

	useEffect(() => {
		const prefersReduced =
			typeof window !== "undefined" &&
			window.matchMedia &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (prefersReduced || isHovered) return;

		const id = setInterval(() => {
			setIndex((i) => i + 1);
		}, 3000);

		return () => clearInterval(id);
	}, [isHovered]);

	const handleTouchStart = (e) => {
		setTouchStart(e.targetTouches[0].clientX);
	};

	const handleTouchMove = (e) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const handleTouchEnd = () => {
		if (touchStart - touchEnd > 50) {
			setIndex((i) => i + 1);
		}
		if (touchStart - touchEnd < -50) {
			setIndex((i) => i - 1);
		}
	};

	const logicalIndex = index % servicesData.length;

	return (
		<div className="w-full">
			{isCarousel ? (
				<div
					className=""
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<div className="flex justify-center items-center gap-6">
						<div
							className="relative w-full overflow-hidden px-4 py-12"
							style={{ perspective: "1000px" }}
						>
							<motion.div
								className="flex"
								style={{ transformStyle: "preserve-3d" }}
								animate={{
									x:
										itemsPerView === 3
											? `calc(-${index * 33.333}% + 33.333%)`
											: `-${index * 100}%`,
								}}
								transition={
									isTransitioning
										? {
												type: "spring",
												stiffness: 200,
												damping: 30,
												mass: 0.5,
										  }
										: { duration: 0 }
								}
								onAnimationComplete={handleAnimationComplete}
								onTouchStart={handleTouchStart}
								onTouchMove={handleTouchMove}
								onTouchEnd={handleTouchEnd}
							>
								{extendedServices.map((service, i) => {
									const active = i === index;
									return (
										<div
											key={`${service.title}-${i}`}
											className="flex-[0_0_100%] md:flex-[0_0_33.333%] px-2 md:px-4 transition-all duration-500"
											style={{ transformStyle: "preserve-3d" }}
										>
											<motion.div
												animate={{
													scale: active ? 1 : 0.82,
													rotateY:
														i === index - 1 ? -6 : i === index + 1 ? 6 : 0,
													y: active ? -10 : 0,
													zIndex: active ? 10 : 0,
												}}
												style={{
													transformStyle: "preserve-3d",
													originX:
														i === index - 1 ? 0 : i === index + 1 ? 1 : 0.5,
												}}
												className="h-full"
											>
												<ServiceCard
													service={service}
													index={i}
													isActive={active}
												/>
											</motion.div>
										</div>
									);
								})}
							</motion.div>
						</div>
					</div>

					<div className="flex justify-center items-center gap-4 mt-6">
						<button
							type="button"
							aria-label="Previous"
							className="place-items-center grid bg-steerOrange shadow-lg p-2 rounded-full text-white text-xl hover:scale-110 transition-transform duration-200"
							onClick={() => setIndex((i) => i - 1)}
						>
							<FaChevronLeft />
						</button>

						<div className="flex justify-center gap-2">
							{servicesData.map((_, i) => (
								<button
									key={i}
									type="button"
									aria-label={`Go to slide ${i + 1}`}
									className={`w-2 h-2 rounded-full transition-all duration-300 ${
										i === logicalIndex ? "bg-steerBlack w-8" : "bg-gray-300"
									}`}
									onClick={() => setIndex(i + servicesData.length)}
								/>
							))}
						</div>

						<button
							type="button"
							aria-label="Next"
							className="place-items-center grid bg-steerOrange shadow-lg p-2 rounded-full text-white text-xl hover:scale-110 transition-transform duration-200"
							onClick={() => setIndex((i) => i + 1)}
						>
							<FaChevronRight />
						</button>
					</div>
				</div>
			) : (
				<motion.div
					variants={containerVariants}
					initial="hidden"
					animate="visible"
					className="flex flex-wrap justify-center gap-x-12 gap-y-6"
				>
					{servicesData.map((service, i) => (
						<ServiceCard key={i} service={service} index={i} />
					))}
				</motion.div>
			)}
		</div>
	);
}

ServicesSection.propTypes = {
	isCarousel: PropTypes.bool,
};

export default ServicesSection;
